import { request } from "./apiClient";
import type {
  RecurringIncomeItem,
  RecurringFrequency,
  TaxIncomeType,
  IncomeCategory,
} from "@boo/shared";

export interface CreateRecurringIncomePayload {
  title: string;
  amount: number;
  frequency: RecurringFrequency;
  dayOfMonth: number;
  applyMonth?: number;
  taxIncomeType?: TaxIncomeType;
  incomeCategory?: IncomeCategory | null;
  senderPattern?: string | null;
  accountId?: string | null;
  startMonth?: number;
  startYear?: number;
  endMonth?: number | null;
  endYear?: number | null;
}

export interface TaxProjection {
  employment40_1: number;
  freelance40_8: number;
}

export interface ApplyResult {
  created: number;
  skipped: number;
}

export async function fetchRecurringIncomes(): Promise<RecurringIncomeItem[]> {
  return request<RecurringIncomeItem[]>("/recurring-income");
}

export async function createRecurringIncome(
  payload: CreateRecurringIncomePayload,
): Promise<RecurringIncomeItem> {
  return request<RecurringIncomeItem>("/recurring-income", {
    method: "POST",
    data: payload,
  });
}

export async function updateRecurringIncome(
  id: string,
  payload: Partial<CreateRecurringIncomePayload>,
): Promise<RecurringIncomeItem> {
  return request<RecurringIncomeItem>(`/recurring-income/${id}`, {
    method: "PATCH",
    data: payload,
  });
}

export async function deleteRecurringIncome(id: string): Promise<void> {
  await request<void>(`/recurring-income/${id}`, { method: "DELETE" });
}

export async function applyRecurringIncomeForPeriod(
  year: number,
  month: number,
): Promise<ApplyResult> {
  return request<ApplyResult>("/recurring-income/apply", {
    method: "POST",
    data: { year, month },
  });
}

export async function fetchRecurringIncomeTaxProjection(
  year: number,
): Promise<TaxProjection> {
  return request<TaxProjection>(
    `/recurring-income/tax-projection?year=${year}`,
  );
}

export interface MonthlyIncomeEntry {
  month: number;
  employment40_1: number;
  freelance40_8: number;
  total: number;
  isProjected: boolean;
}

export interface MonthlyBreakdown {
  year: number;
  months: MonthlyIncomeEntry[];
}

export async function fetchRecurringIncomeMonthlyBreakdown(
  year: number,
): Promise<MonthlyBreakdown> {
  return request<MonthlyBreakdown>(
    `/recurring-income/monthly-breakdown?year=${year}`,
  );
}
